/**
 * Meteor logic, pure: unlocking, picking a kind, a meteor's health and
 * countdown, and paying out shards. The canvas (ui/nest.ts) owns where a
 * meteor is and how it looks; this owns whether it breaks.
 */

import {
  BASE_CLICK_DAMAGE,
  METEOR_KINDS,
  METEOR_SPAWN_MAX_SECONDS,
  METEOR_SPAWN_MIN_SECONDS,
  METEOR_UNLOCK_ACHIEVEMENTS,
  SKY_TIERS,
} from '../data/meteors';
import type { MeteorKind } from '../data/meteors';
import { SHOP_ITEMS_BY_ID } from '../data/shop';
import type { GameState } from './state';
import { eraModifier, skyHaste } from './rebirth';

/** Meteors fall and the shop is open once this many achievements are earned. */
export function isMeteorsUnlocked(state: GameState): boolean {
  return state.achievements.length >= METEOR_UNLOCK_ACHIEVEMENTS;
}

/**
 * The weights in force right now: the last sky tier whose tool the player
 * owns. Tiers are listed in order, so the best tool wins.
 */
export function meteorWeights(state: GameState): Readonly<Record<string, number>> {
  let weights = SKY_TIERS[0]!.weights;
  for (const tier of SKY_TIERS) {
    if (tier.tool === null || state.shop.includes(tier.tool)) weights = tier.weights;
  }
  return weights;
}

/** Picks a kind by weight from a roll in [0, 1). */
export function pickMeteorKind(
  roll: number,
  weights: Readonly<Record<string, number>> = SKY_TIERS[0]!.weights,
  kinds: readonly MeteorKind[] = METEOR_KINDS,
): MeteorKind {
  const weightOf = (k: MeteorKind): number => Math.max(0, weights[k.id] ?? 0);
  const total = kinds.reduce((sum, k) => sum + weightOf(k), 0);
  // A sky with nothing in it (every weight zero) still has to return a kind.
  if (total <= 0) return kinds[kinds.length - 1]!;
  let r = Math.max(0, Math.min(0.999999, roll)) * total;
  for (const kind of kinds) {
    r -= weightOf(kind);
    if (r < 0) return kind;
  }
  // Only reachable on floating-point slop at the very top of the range.
  for (let i = kinds.length - 1; i >= 0; i--) {
    if (weightOf(kinds[i]!) > 0) return kinds[i]!;
  }
  return kinds[kinds.length - 1]!;
}

/** How many of this kind fall together, from a roll in [0, 1). */
export function showerSize(kind: MeteorKind, roll: number): number {
  if (!kind.shower) return 1;
  const { min, max } = kind.shower;
  return min + Math.floor(Math.max(0, Math.min(0.999999, roll)) * (max - min + 1));
}

/** One fall: the kind picked from the player's sky, and one meteor per rock. */
export function rollMeteorFall(state: GameState, random: () => number = Math.random): Meteor[] {
  const kind = pickMeteorKind(random(), meteorWeights(state));
  const count = showerSize(kind, random());
  return Array.from({ length: count }, () => createMeteor(kind));
}

/** Damage one click does to a meteor: the best tool owned, or the bare hand. */
export function clickDamage(state: GameState): number {
  let damage = BASE_CLICK_DAMAGE;
  for (const id of state.shop) {
    const e = SHOP_ITEMS_BY_ID.get(id)?.effect;
    if (e?.kind === 'damage' && e.damage > damage) damage = e.damage;
  }
  return damage;
}

/**
 * Seconds between falls under this era, or null when the era has no meteors
 * at all. A modifier's gap replaces the range rather than scaling it.
 */
export function meteorGap(
  state: GameState,
  now: number = Date.now(),
): { min: number; max: number } | null {
  const haste = skyHaste(state, now);
  const era = eraModifier(state);
  if (era && 'meteorGap' in era) {
    if (era.meteorGap === null) return null;
    if (era.meteorGap !== undefined) {
      return { min: era.meteorGap / haste, max: era.meteorGap / haste };
    }
  }
  return { min: METEOR_SPAWN_MIN_SECONDS / haste, max: METEOR_SPAWN_MAX_SECONDS / haste };
}

/**
 * How much faster meteors fall than the base 2–5 minutes: 1 until the shop's
 * sky items are bought. The spawn clock is advanced by this much, so a
 * purchase takes effect on the interval already running.
 */
export function meteorRate(state: GameState): number {
  let rate = 1;
  for (const id of state.shop) {
    const e = SHOP_ITEMS_BY_ID.get(id)?.effect;
    if (e?.kind === 'meteorRate') rate *= e.mult;
  }
  return rate;
}

export type MeteorStatus = 'sitting' | 'broken' | 'cooled';

export interface Meteor {
  kind: MeteorKind;
  health: number;
  /** Seconds left on the countdown. */
  secondsLeft: number;
  status: MeteorStatus;
}

export function createMeteor(kind: MeteorKind): Meteor {
  return { kind, health: kind.health, secondsLeft: kind.countdown, status: 'sitting' };
}

/** A click on the meteor. Returns the status afterwards. */
export function hitMeteor(meteor: Meteor, damage: number): MeteorStatus {
  if (meteor.status !== 'sitting') return meteor.status;
  meteor.health = Math.max(0, meteor.health - Math.max(0, damage));
  if (meteor.health === 0) meteor.status = 'broken';
  return meteor.status;
}

/** Runs the countdown. Returns the status afterwards. */
export function tickMeteor(meteor: Meteor, seconds: number): MeteorStatus {
  if (meteor.status !== 'sitting') return meteor.status;
  meteor.secondsLeft = Math.max(0, meteor.secondsLeft - Math.max(0, seconds));
  if (meteor.secondsLeft === 0) meteor.status = 'cooled';
  return meteor.status;
}

/** Credits a broken meteor's shards. Returns the shards paid (0 unless broken). */
export function awardShards(state: GameState, meteor: Meteor): number {
  if (meteor.status !== 'broken') return 0;
  const shards = Math.round(meteor.kind.shards * (eraModifier(state)?.shards ?? 1));
  state.shards += shards;
  state.shardsEarned += shards;
  state.meteorsBroken += 1;
  state.meteorKindsBroken[meteor.kind.id] = (state.meteorKindsBroken[meteor.kind.id] ?? 0) + 1;
  return shards;
}
